import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Checkbox, Collapse, Flex, Form, Input, Layout, Modal, Popconfirm, Progress, Radio, Space, Tabs, Tag, Tooltip, Typography } from 'antd';
import {  CloseOutlined, DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Routes } from '../../../routes';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import dayjs from 'dayjs';
import { useCreateLeaderboardMutation, useDeleteLeaderboardMutation, useLeaderboardAudienceQuery, useLeaderboardQuery, useLeaderboardTypesQuery, usePatchLeaderboardMutation, useSaveLeaderboardAudiencesMutations } from '../../gql/hooks/useLeaderboardQueries';
import { DEFAULT_LOCALE_CODE } from '../../../const';
import { LanguageSelectorCMX } from '../../../components/formItems';
import BasicsTab from '../GenericCreateEdit/BasicsTab';
import Sidebar from '../GenericCreateEdit/SidebarActions';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import GenericTab, { TAB_STATES } from '../GenericCreateEdit/GenericTab';
import { useSegmentation } from '../../../hooks/useSegmentation';
import ExpandableAlert from '../../../components/errors/ExpandableAlert';
import classNames from 'classnames';
import { DateRangeSelectorFormItem } from '../GenericCreateEdit/DateRangeSelector';
import { ColoredTag } from '../GenericCreateEdit/GenericElements';
import { useCreateEditGeneric } from '../GenericCreateEdit/useCreateEditGeneric';
import { formatDTPayload, parseDTPayload } from '../../utils';

dayjs.extend( localizedFormat );
dayjs.extend( utc );
dayjs.extend( timezone );
dayjs.tz.guess();


const KEY = {
    ALL: 'ALL',
    MONTHLY: 'MONTHLY',
    YEARLY: 'YEARLY',
    COURSE_COMPLETED: 'COURSE_COMPLETED',
    COURSE_STARTED: 'COURSE_STARTED',
};
const GRANULARITY = {
    [ KEY.ALL ]: 'All Time',
    [ KEY.MONTHLY ]: 'Monthly',
    [ KEY.YEARLY ]: 'Yearly'
};

function formatLocaleForm( t ) {
    return { locale: t.code, name: t.name, description: t.description };
}

function formatLeaderboardForm( l ) {
    const tz = l?.timezone ?? dayjs.tz.guess();
    return {
        translations: l.locales.map( formatLocaleForm ),
        timezone: tz,
        granularity: l.granularity,
        isPublished: l.isPublished,
        isFeatured: false,
        leaderboardTypeId: l.leaderboardTypeId,
        // Stored as UTC; the pickers hold a floating wall clock in the selected timezone.
        startAt: parseDTPayload( l.startAt, tz ),
        endAt: parseDTPayload( l.endAt, tz ),
        audiences: l.audiences?.map( formatAudienceForm ) ?? [],
    };
}

function formatAudienceForm( a ) {
    if ( !a ) {
        return null;
    }
    return {
        id: a.id,
        translations: a.translations ?? a.locales.map( formatLocaleForm ), // an already formatted audience will have translations but not locales
        brandIds: a.brandIds,
        categoryIds: a.categoryIds,
        companyRelationshipIds: a.companyRelationshipIds,
        employeeLocaleIds: a.employeeLocaleIds,
        learningProfileIds: a.learningProfileIds,
        localeIds: a.localeIds,
        productIds: a.productIds,
        segmentIds: a.segmentIds,
        isPublished: a.isPublished,
        isFeatured: a.isFeatured,
    };
}

function formatLeaderboardInput( l ) {
    return {
        leaderboardId: l.id,
        leaderboardTypeId: l.leaderboardTypeId,
        name: l.translations?.find( t => t.locale == DEFAULT_LOCALE_CODE ).name,
        description: l.translations?.find( t => t.locale == DEFAULT_LOCALE_CODE ).description,
        granularity: l.granularity,
        isPublished: l.isPublished,
        isFeatured: l.isFeatured,
        timezone: l.timezone,
        // The picker holds a floating wall clock in the selected timezone; reinterpret it in
        // that zone before converting to UTC. `?? undefined` keeps an empty date omitted from
        // the patch input rather than sending an explicit null.
        startAt: formatDTPayload( l.startAt, l.timezone ) ?? undefined,
        endAt: formatDTPayload( l.endAt, l.timezone ) ?? undefined,
        locales: l.translations?.map( t => ( { code: t.locale, name: t.name, description: t.description } ) ),
    };
}

const creationInitialValues = {
    translations: [ { locale: 'en-US' } ],
    timezone: dayjs.tz.guess(),
    granularity: KEY.ALL,
    isPublished: false,
    isFeatured: false,
    leaderboardTypeId: null,
    startAt: null,
    endAt: null,
    audiences: null,
};

const creationTabStates = {
    basics: [ TAB_STATES.REQUIRED ],
    leaderboard: [ TAB_STATES.REQUIRED ],
    audiences: [ TAB_STATES.LOCKED ],
};

const CreateEditLeaderboardPage = ( { route: { params: { leaderboardId } } } ) => {

    const basicsRef = useRef(), leaderboardRef = useRef(), audienceRef = useRef();

    const [ fetchLeaderboard ] = useLeaderboardQuery();
    const [ createLeaderboardMutation ] = useCreateLeaderboardMutation();
    const [ patchLeaderboardMutation ] = usePatchLeaderboardMutation();
    const [ deleteLeaderboardMutation ] = useDeleteLeaderboardMutation();

    const {
        isCreating, loading, error, setActiveTab, activeTab, isPublished,
        form,
        itemId,
        getTabIcon, tabStates,
        onValidateTab,
        messageContext,
        sidebarProps, tabProps,
    } = useCreateEditGeneric( {
        id: leaderboardId, idName: 'leaderboardId',
        label: 'leaderboard',
        editRoute: Routes.EditLeaderboard, viewRoute: Routes.ViewLeaderboardsPage,
        creationTabStates, creationInitialValues,
        createMutation: createLeaderboardMutation, 
        fetch: fetchLeaderboard,
        patchMutation: patchLeaderboardMutation, 
        deleteMutation: deleteLeaderboardMutation,
        formatDataToForm: formatLeaderboardForm, formatFormToData: formatLeaderboardInput
    } );

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Incentives', path: Routes.GamificationNav.buildPath() },
        { name: 'Leaderboards', path: Routes.ViewLeaderboardsPage.buildPath() },
        { name: isCreating ? 'Create' : 'Edit' }
    ];

    const audienceList = Form.useWatch( 'audiences', form );

    const { canPublish, publishTooltip } = useMemo( () => {

        if ( !audienceList?.length && !isPublished ) {
            return { canPublish: false, publishTooltip: 'Leaderboard must have at least one audience before it is published' };
        }

        return { canPublish: sidebarProps?.canPublish };
    }, [ sidebarProps?.canPublish, audienceList, isPublished ] );

    const tabItems = useMemo( () => [
        {
            key: 'basics',
            label: 'Basics',
            icon: getTabIcon( tabStates.basics ),
            children: <Space className='body'>
                <BasicsTab { ...tabProps } ref={ basicsRef } statuses={ tabStates.basics } onValidateCb={ statuses => onValidateTab( 'basics', statuses ) } disabled={ !!error } />
                <Sidebar { ...sidebarProps } tab='basics' tabFields={ basicsRef?.current?.formFields }  canPublish={ canPublish } publishTooltip={ publishTooltip } disabled={ !!error } />
            </Space>
        },
        {
            key: 'leaderboard',
            label: 'Leaderboard',
            icon: getTabIcon( tabStates.leaderboard ),
            children: <Space className='body'>
                <LeaderboardTab { ...tabProps } ref={ leaderboardRef } statuses={ tabStates.leaderboard } onValidateCb={ statuses => onValidateTab( 'leaderboard', statuses ) } disabled={ !!error } />
                <Sidebar { ...sidebarProps } tab='leaderboard' tabFields={ leaderboardRef?.current?.formFields }  canPublish={ canPublish } publishTooltip={ publishTooltip } disabled={ !!error } />
            </Space>
        },
        {
            key: 'audiences',
            label: 'Audiences',
            icon: getTabIcon( tabStates.audiences ),
            disabled: isCreating,
            children: <Space className='body'>
                <AudienceTab { ...tabProps } ref={ audienceRef } isPublished={ isPublished } leaderboardId={ itemId } statuses={ tabStates.audiences } disabled={ !!error } />
                <Sidebar { ...sidebarProps } tab='audiences' tabFields={ audienceRef?.current?.formFields } 
                    saveTooltip='Changes to audiences are always saved automatically'
                    canPublish={ canPublish } publishTooltip={ publishTooltip } />
            </Space>,
        },
    ], [ tabProps, isCreating, tabStates, sidebarProps, canPublish, publishTooltip, isPublished, itemId ] );

    return (
        <Layout className="leaderboard-edit-page">

            { messageContext }

            <Breadcrumbs crumbs={ breadcrumbs } />

            <Card
                className="leaderboard-edit-content"
                title={ isCreating ? 'Create Leaderboard' : 'Edit Leaderboard' }
            >
                { error && error }
                
                <Form form={ form } layout='vertical' >

                    <Tabs
                        type="card"
                        size="large"
                        activeKey={ activeTab }
                        onChange={ setActiveTab }
                        items={ tabItems }
                        tabBarExtraContent={ {
                            right: loading ? <></> : <ColoredTag.Publish isPublished={ isPublished } />
                        } }
                    />
                </Form>

            </Card>

        </Layout>
    );
};

const LeaderboardTab = ( { ref, form, statuses, parentError, loading, onValidateCb, disabled } ) => {
    const formNames = [ 'startAt', 'endAt', 'granularity', 'leaderboardTypeId', 'timezone' ];

    const leaderboardTypes = useLeaderboardTypesQuery();

    return <GenericTab ref={ ref } loading={ loading } parentError={ parentError } statuses={ statuses } title='Leaderboard Information' onValidateCb={ onValidateCb } form={ form } formNames={ formNames }>
        { ( { validateTab } ) => ( <>
            <DateRangeSelectorFormItem
                startRequired={ false } endRequired={ false } disabled={ disabled } validateTab={ validateTab } form={ form }
                helpMessage={ 'Start and End date of the leaderboard. With no Start Date all historic data is used. With no End Date all current and future data is used.' }
                />
            <Card
                size="small"
                title={ 'Granularity' }
                extra={ <Tooltip title='Divisions of time that users can be ranked on' ><QuestionCircleOutlined /></Tooltip> }
            >
                <Form.Item
                    label='Available Granularities'
                    name='granularity'
                >
                    <GranularitySelector disabled={ disabled } onChange={ validateTab } />
                </Form.Item>
            </Card>
            <Card
                size='small'
                title={ 'Leaderboard Type' }
                loading={ leaderboardTypes === null }
                extra={ <Tooltip title='What data the leaderboard will rank users on' ><QuestionCircleOutlined /></Tooltip> }
            >
                <Form.Item
                    label='Data Units'
                    name='leaderboardTypeId'
                    rules={ [
                        {
                            required: true,
                            message: 'Leaderboard type is required'
                        }
                    ] }
                >
                    <Radio.Group
                        onChange={ validateTab } disabled={ disabled }
                        options={ leaderboardTypes?.map( l => ( { value: l.leaderboardTypeId, label: l.name } ) ) }
                    />
                </Form.Item>
            </Card>
        </> ) }
    </GenericTab>;
};

const GranularitySelector = ( { value, onChange, disabled } ) => {

    return <Space direction='vertical' style={ { marginTop: 5 } }>
        <Checkbox disabled={ disabled } onChange={ () => onChange( KEY.ALL ) } indeterminate={ [ KEY.YEARLY, KEY.MONTHLY ].includes( value ) } checked={ value == KEY.ALL }>{ GRANULARITY.ALL }</Checkbox>
        <Checkbox disabled={ disabled } onChange={ () => onChange( KEY.YEARLY ) } indeterminate={ [ KEY.MONTHLY ].includes( value ) } checked={ value == KEY.YEARLY }>{ GRANULARITY.YEARLY }</Checkbox>
        <Checkbox disabled={ disabled } onChange={ () => onChange( KEY.MONTHLY ) } checked={ value == KEY.MONTHLY }>{ GRANULARITY.MONTHLY }</Checkbox>
    </Space>;
};

const AudienceTab = ( { form, statuses, loading: tabLoading, leaderboardId, disabled, softUpdateGeneric, isPublished } ) => {

    return <GenericTab statuses={ statuses } loading={ tabLoading } title='Audiences' className='audience-tab' >
        <Form.Item name='audiences'>
            <AudienceFormItem onChange={ audiences => softUpdateGeneric( { audiences } ) } disabled={ disabled } leaderboardId={ leaderboardId } isPublished={ isPublished } />
        </Form.Item>
    </GenericTab>;
};

const AudienceFormItem = ( { value, onChange, disabled, leaderboardId, isPublished } ) => {

    function addAudience( audience ) {
        const newList = [ ...value, audience ];
        onChange( newList );
    }

    function saveAudience( audience ) {
        const newList = value.map( a => a.id == audience.id ? audience : a );
        onChange( newList );
    }

    function removeAudience( audience ) {
        const newList = value.filter( a => a.id !== audience.id );
        onChange( newList );
    }

    return ( <Flex dir='horizontal' wrap name='audiences'>
        { ( value ?? [] ).map( a =>
            <AudienceCard key={ a.id } disabled={ disabled } audience={ a } remove={ removeAudience } save={ saveAudience } leaderboardId={ leaderboardId } isPublished={ isPublished } />
        ) }

        <AudienceCard disabled={ disabled } add={ addAudience } save={ saveAudience } leaderboardId={ leaderboardId } isPublished={ isPublished } isCreateButton />
    </Flex> );
};

const AudienceCard = ( { disabled, add: addAudience, remove: removeAudience, save: saveAudience, audience, leaderboardId, onFinish: onFinishCb, isPublished, isCreateButton = false } ) => {

    const [ form ] = Form.useForm();
    const audiences = Form.useWatch( 'audiences' );

    const { renderSegmentation, saveSegmentation } = useSegmentation( form, { updatedBrandsLocation: false, defaultSelectBrand: false } );

    useEffect( () => {
        form.setFieldsValue( audience );
    }, [ audience ] );
    const [ , { create, deleteAudiences, patch } ] = useSaveLeaderboardAudiencesMutations();
    const [ fetchAudience ] = useLeaderboardAudienceQuery();

    const [ excludedLocales, setExcludedLocales ] = useState( [] );
    const [ loading, setLoading ] = useState( false );
    const [ error, setError ] = useState();
    const [ open, setOpen ] = useState( false );

    const [ progress, setProgress ] = useState( 0 );
    const [ progressStatus, setProgressStatus ] = useState();

    const [ name, description ] = useMemo( () => {
        const trans = audience?.translations?.find( t => t.locale == DEFAULT_LOCALE_CODE );
        return [
            trans?.name,
            trans?.description
        ];
    }, [ audience ] );

    const disableDelete = useMemo( () => isPublished && audiences?.length === 1, [ isPublished, audiences ] );

    function close() {
        setOpen( false );
        setError();
        setProgress( 0 );
        setProgressStatus();
        form.resetFields();
    }

    function onFinish( afterFinishCb ) {
        onFinishCb?.();
        setTimeout( () => {
            close();
            setLoading( false );
            afterFinishCb?.();
        }, 2000 );
    }

    function onClick() {
        if ( !open && !disabled ) {
            if ( isCreateButton ) {
                form.setFieldValue( [ 'translations', 0, 'locale' ], DEFAULT_LOCALE_CODE );
            }
            setOpen( true );
        }
    }

    function prerequestReset() {
        setError();
        setProgress( 1 );
        setProgressStatus();
        setLoading( true );
    }

    async function handleDeleteClick() {
        if ( disableDelete ) {
            return;
        }

        prerequestReset();

        try {
            const result = await deleteAudiences( leaderboardId, audience.id );

            setProgress( 90 );

            if ( !result.success ) {
                throw result;
            } else {
                setProgress( 100 );
                onFinish( () => removeAudience( audience ) );
            }
        } catch ( e ) {
            console.error( e );
            setProgress( 50 );
            setProgressStatus( 'exception' );
            setError( <ExpandableAlert message='Could not delete audience' error={ e } /> );
            setLoading( false );
        }
    }

    function handleCancelClick() {
        close();
    }

    async function handleOkClick() {
        prerequestReset();

        await form.validateFields()
            .then( async vals => {

                setProgress( 25 );

                let audienceId, success = true;
                const formAudience = vals;

                const audienceInput = {
                    leaderboardId,
                    name: formAudience.translations.find( t => t.locale == DEFAULT_LOCALE_CODE ).name,
                    description: formAudience.translations.find( t => t.locale == DEFAULT_LOCALE_CODE ).description,
                    locales: formAudience.translations.map( t => ( { code: t.locale, name: t.name, description: t.description } ) ),
                };

                const segmentationInput = {
                    brandIds: formAudience.brandIds,
                    categoryIds: formAudience.categoryIds,
                    companyRelationshipIds: formAudience.companyRelationshipIds,
                    employeeLocaleIds: formAudience.employeeLocaleIds,
                    learningProfileIds: formAudience.learningProfileIds,
                    localeIds: formAudience.localeIds,
                    productIds: formAudience.productIds,
                    segmentIds: formAudience.segmentIds
                };

                // 1) add/save the unsegmented audience to the leaderboard
                try {
                    let saveAudienceResult;
                    if ( isCreateButton ) {
                        saveAudienceResult = await create( audienceInput );
                        audienceId = saveAudienceResult.data.leaderboardAudienceId;
                        addAudience( formatAudienceForm( saveAudienceResult.data ) );
                    } else {
                        audienceInput.leaderboardAudienceId = audience.id;
                        audienceId = audience.id;
                        saveAudienceResult = await patch( audienceInput );
                        saveAudience( formatAudienceForm( saveAudienceResult.data ) );
                    }

                    setProgress( 60 );
                } catch ( e ) {
                    console.error( e );
                    setError( <ExpandableAlert message='Could not save audience' error={ e } /> );
                    setLoading( false );
                    setProgressStatus( 'exception' );
                    return;
                }

                // 2) add segmentation to newly created/saved audience
                try {
                    await saveSegmentation( segmentationInput, audienceId, 'leaderboard-audience' );

                    setProgress( 90 );
                } catch ( e ) {
                    console.error( e );
                    setError( <ExpandableAlert message='Could not save segmentation to audience' error={ e } /> );
                    setLoading( false );
                    success = false;
                    setProgressStatus( 'exception' );
                    // dont quit out because we still need to addAudience if applicable
                }

                // 3) udpate UI with new segmented audience
                try {
                    const result = await fetchAudience( { leaderboardId, audienceId } );

                    const fetchedAudience = formatAudienceForm( result.data );

                    if ( !fetchedAudience ) {
                        throw result;
                    }

                    if ( isCreateButton ) {
                        // add a "second" time but really were jsut updating the UI again before the state actually changes
                        // no I cant just always the saveAudience - I tried
                        addAudience( fetchedAudience ); 
                    } else {
                        saveAudience( fetchedAudience );
                    }

                    setProgress( 100 );


                } catch ( e ) {
                    console.error( e );
                    setError( <ExpandableAlert message='Could not refetch updated audience' error={ e } /> );
                    success = false;
                    setProgressStatus( 'exception' );
                }

                if ( success ) {
                    onFinish();
                }

            } ).catch( e => {
                console.error( e );
                setError( <ExpandableAlert message='Could not save audience' error={ e } /> );
                setProgressStatus( 'exception' );
                setLoading( false );
            } );

    }

    function onLocaleChange() {
        const usedLocales = form.getFieldValue( 'translations' ).map( t => t?.locale );
        setExcludedLocales( usedLocales );
    }

    return <>
        { !isCreateButton &&
            <Card
                loading={ loading || disabled }
                className={ classNames( 'audience-card' ) }
                title={ name }
                extra={ loading || disabled ? null :
                    <Button
                        type="text"
                        color='blue'
                        disabled={ disabled }
                        icon={ <EditOutlined /> }
                        onClick={ onClick }
                    />
                }
            >
                <Typography.Paragraph ellipsis={ { rows: 2 } }>
                    <strong>Description: </strong> { description?.length > 0 ? <span>{ description }</span> : <span className='canceled'>No description</span> }
                </Typography.Paragraph>
                <h4>Translated Locales:</h4>
                <div>
                    { audience.translations.map( t => t.locale ).map( l => <Tag key={ l } color={ l == 'en-US' ? 'green' : 'blue' }>{ l }</Tag> ) }
                </div>
                <h4>Segmentation:</h4>
                { [ audience.brandIds, audience.categoryIds, audience.companyRelationshipIds, audience.employeeLocaleIds,
                audience.learningProfileIds, audience.localeIds, audience.productIds, audience.segmentIds ].every( l => !l?.length > 0 ) ?
                    <span>No segmentation defined</span> :
                    <ul>
                        { audience.brandIds?.length > 0 && <li><strong>Brands: </strong>{ audience.brandIds.length }</li> }
                        { audience.categoryIds?.length > 0 && <li><strong>Categories: </strong>{ audience.categoryIds.length }</li> }
                        { audience.companyRelationshipIds?.length > 0 && <li><strong>Company Relationships: </strong>{ audience.companyRelationshipIds.length }</li> }
                        { audience.employeeLocaleIds?.length > 0 && <li><strong>Employee Locales: </strong>{ audience.employeeLocaleIds.length }</li> }
                        { audience.learningProfileIds?.length > 0 && <li><strong>Learning Profiles: </strong>{ audience.learningProfileIds.length }</li> }
                        { audience.localeIds?.length > 0 && <li><strong>Customer Locales: </strong>{ audience.localeIds.length }</li> }
                        { audience.productIds?.length > 0 && <li><strong>Products: </strong>{ audience.productIds.length }</li> }
                        { audience.segmentIds?.length > 0 && <li><strong>Segments: </strong>{ audience.segmentIds.length }</li> }
                    </ul>
                }
            </Card>
        }
        { isCreateButton &&
            <Button
                type="dashed"
                disabled={ disabled }
                icon={ <PlusOutlined /> }
                onClick={ onClick }
                className='add-item-button'
            >
                Add Audience
            </Button>
        }
        <Modal
            title={ isCreateButton ? 'Add Audience' : 'Edit Audience' }
            closeIcon={ null }
            destroyOnHidden={ true }
            open={ open }
            width={ {
                xs: '95vw',
                lg: '900px'
            } }
            footer={ [
                ( isCreateButton ? <></> :
                    <Popconfirm
                        title="Are you sure you want to delete this audience?"
                        onConfirm={ handleDeleteClick }
                        okText="Yes"
                        cancelText="No">
                        <Button danger className='delete' disabled={ loading || disableDelete } icon={ <DeleteOutlined /> }>
                            Delete
                        </Button>
                    </Popconfirm> ),
                <Button key='cancel' onClick={ handleCancelClick } icon={ <CloseOutlined /> } disabled={ loading }>
                    Cancel
                </Button>,
                <Button key='save' color='primary' type='primary' onClick={ handleOkClick } loading={ loading } >
                    Save
                </Button>,
                progress > 0 ? <Progress percent={ progress } status={ progressStatus } showInfo={ !!progressStatus } /> : <></>
            ] }
            maskClosable={ false }
        >
            <Form form={ form } layout='vertical' initialValues={ isCreateButton ? { translations: [ { locale: DEFAULT_LOCALE_CODE } ] } : audience } >
                <Form.Item required label="Locale" name={ [ 'translations', 0, 'locale' ] }
                    rules={ [
                        {
                            required: true,
                            message: 'Localization must have locale'
                        }
                    ] }>
                    <LanguageSelectorCMX
                        mode="select-single"
                        disabled={ true }
                    />
                </Form.Item>

                <Form.Item required label="Name" name={ [ 'translations', 0, 'name' ] }
                    rules={ [
                        {
                            required: true,
                            message: 'Localization must have name'
                        }
                    ] }>
                    <Input
                        disabled={ loading }
                        autoComplete='off'
                        placeholder="Name" />
                </Form.Item>

                <Form.Item label="Description" name={ [ 'translations', 0, 'description' ] } >
                    <Input.TextArea
                        disabled={ loading }
                        rows={ 3 }
                        placeholder="Description" />
                </Form.Item>

                <Collapse
                    disabled={ loading }
                    items={ [ {
                        key: '1',
                        label: 'Additional translations',
                        children: <>
                            <Form.List
                                name={ [ 'translations' ] }>
                                { ( fields, { add } ) => (
                                    <>
                                        { fields.filter( ( subfield ) => subfield.key !== 0 ).map( ( subfield ) => {
                                            const locale = form.getFieldValue( [ 'translations', subfield.name, 'locale' ] );
                                            return <Card
                                                key={ subfield.key }
                                                size='small'
                                                disabled={ loading }
                                            >
                                                <Form.Item required label="Locale" name={ [ subfield.name, 'locale' ] }
                                                    rules={ [
                                                        {
                                                            required: true,
                                                            message: 'Localization must have locale'
                                                        }
                                                    ] }>
                                                    <LanguageSelectorCMX
                                                        mode="select-single"
                                                        disabled={ loading }
                                                        excludelocales={ excludedLocales.filter( l => l !== locale ) }
                                                        hideExcludedLocales={ false }
                                                        onChange={ () => onLocaleChange() }
                                                    />
                                                </Form.Item>

                                                <Form.Item required label="Name" name={ [ subfield.name, 'name' ] }
                                                    rules={ [
                                                        {
                                                            required: true,
                                                            message: 'Localization must have name'
                                                        }
                                                    ] }>
                                                    <Input
                                                        disabled={ loading }
                                                        autoComplete='off'
                                                        placeholder="Name" />
                                                </Form.Item>

                                                <Form.Item label="Description" name={ [ subfield.name, 'description' ] } >
                                                    <Input.TextArea
                                                        rows={ 3 }
                                                        disabled={ loading }
                                                        placeholder="Description" />
                                                </Form.Item>
                                            </Card>;
                                        }
                                        ) }
                                        <Button
                                            type="dashed"
                                            icon={ <PlusOutlined /> }
                                            disabled={ loading }
                                            onClick={ () => { onLocaleChange(); add(); } }
                                            style={ { width: '100%' } }
                                        >
                                            Add Localization
                                        </Button>
                                    </>
                                ) }
                            </Form.List>
                        </>
                    } ] }
                />

                { renderSegmentation( false, loading || disabled ) }
                { error && error }
                { disableDelete && !isCreateButton && <div style={ { fontStyle: 'italic', color: '#aaa', textAlign: 'end' } }>Published leaderboards need at least one audience. In order to delete this audience, unpublish the leaderboard.</div> }
            </Form>
        </Modal>
    </>;
};

export default CreateEditLeaderboardPage;
